import {
  Grid,
  CardHeader,
  CardContent,
  Box,
  TextField,
} from "@mui/material";
import React, { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Form, FormikProvider, useFormik } from "formik";
import errorHandler from "../../utils/errorHandler";
import LoadingButton from "@mui/lab/LoadingButton";
import CardLayout from "../../layouts/CardLayout";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import { CustomerSchema } from "../../utils/Schema";
import ApiService from "../../services/categoryManagementApi";
import PhoneInput from "react-phone-input-2";
import axios from "axios";

const { customerManagementApi } = ApiService;

const GOOGLE_API_KEY = "AIzaSyDsC3bmbBV7x9bIFL1KvwMi_Yk8-hL16Y8";


function AddCustomer() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { id } = useParams();
  const [data, setData] = useState();
  const [latLng, setLatLng] = useState({ lat: "", lng: "" });
  const [phoneUpdate, setPhoneUpdate] = useState("");
  const addressRefs = {
    houseNumber: useRef(null),
    city: useRef(null),
    village: useRef(null),
    state: useRef(null),
  };


  useEffect(() => {
      const loadGoogleMapsScript = () => {
        if (window.google && window.google.maps) {
          initializeAutocomplete();
          return;
        }
  
        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = initializeAutocomplete;
        document.body.appendChild(script);
      };
  
      const initializeAutocomplete = () => {
        Object.keys(addressRefs).forEach((field) => attachAutocomplete(field));
      };
  
      const attachAutocomplete = (fieldName) => {
        if (!addressRefs[fieldName]?.current) return;
  
        const autocomplete = new window.google.maps.places.Autocomplete(
          addressRefs[fieldName].current,
          { types: ["geocode"] }
        );
  
        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          const allFields = {
            houseNumber: "",
            city: "",
            village: "",
            district: "",
            commune: "",
            streetNumber: "",
            postalCode: ""
          };
  
          place.address_components.forEach((component) => {
            const types = component.types;
  
            if (types.includes("street_number") || types.includes("sublocality_level_3")) {
              allFields.houseNumber = component.long_name;
            }
            if (types.includes("route") || types.includes("sublocality_level_2") || types.includes("locality")) {
              allFields.streetNumber = component.long_name;
            }
            if (types.includes("sublocality") || types.includes("sublocality_level_1") || types.includes("neighborhood")) {
              allFields.village = component.long_name;
            }
            if (types.includes("administrative_area_level_2")) {
              allFields.district = component.long_name;
            }
            if (types.includes("administrative_area_level_3")) {
              allFields.commune = component.long_name;
            }
            if (types.includes("administrative_area_level_1")) {
              allFields.city = component.long_name;
            }
            if (types.includes("postal_code")) {
              allFields.postalCode = component.long_name;
            }
          });
  
          formik.setValues((prev) => ({
            ...prev,
            ...allFields,
          }));
  
          if (place.geometry) {
            setLatLng({
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            });
          }
        });
      };
  
      loadGoogleMapsScript();
    }, []);

  const fetchTaskData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await customerManagementApi.getById({ id });
        const resData = response?.data?.data;
        if (resData?.phoneNumber && resData?.countryCode) {
          setPhoneUpdate(`${resData?.countryCode}${resData?.phoneNumber}`);
        }

        setData(resData);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchTaskData();
  }, [fetchTaskData]);

const getFullAddress = () => {
    const { houseNumber, streetNumber, district, city, village, commune, postalCode } =
      formik.values;

    const fullAddress = `${houseNumber || ""} ${streetNumber || ""} ${district || ""} ${city || ""
      } ${village || ""} ${commune || ""} ${postalCode || ""}`.trim();

    return fullAddress;
  };


  const getLatLngFromAddress = async (address) => {
    try {
      let lat = '';
      let lng = '';
      const response = await axios.get('https://maps.googleapis.com/maps/api/geocode/json', {
        params: {
          address,
          key: GOOGLE_API_KEY,
        },
      });
      const location = response?.data?.results?.[0]?.geometry?.location;

      if (location) {
        lat = location?.lat;
        lng = location?.lng;
      }
      if (response?.status == 200) {
        return { lat, lng };
      } else {
        throw new Error("Geocoding failed: " + data.status);
      }
    } catch (error) {
      console.error("Error fetching geocode:", error);
      return null;
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: data?.name || "",
      email: data?.email || "",
      phoneNumber: data?.phoneNumber || "",
      countryCode: data?.countryCode || "",
      registrationNumber: data?.registrationNumber || "",
      houseNumber: data?.houseNumber || "",
      streetNumber: data?.streetNumber || "",
      village: data?.village || "",
      district: data?.district || "",
      commune: data?.commune || "",
      city: data?.city || "",
      postalCode: data?.postalCode || "",
    },
    validationSchema: CustomerSchema,
    onSubmit: async (values) => {
      setLoading(true);

      try {
        let { phoneNumber, countryCode } = values;
        if (!countryCode.startsWith("+")) {
          countryCode = `+${countryCode}`;
        }

        if (phoneNumber.startsWith(countryCode.replace("+", ""))) {
          phoneNumber = phoneNumber.replace(countryCode.replace("+", ""), "").trim();
        }

        if (phoneNumber.startsWith("0")) {
          phoneNumber = phoneNumber.slice(1);
        }

        const fullAddress = getFullAddress();
        const latlong = await getLatLngFromAddress(fullAddress);

        setLatLng(latlong)
        if (
          (!latLng.lat || !latLng.lng) &&
          (!latlong?.lat || !latlong?.lng)
        ) {
          toast.error("Failed to retrieve location.");
          setLoading(false);
          return;
        }

        const payload = id
          ? {
            ...values, phoneNumber, countryCode, lat: latLng.lat || latlong.lat,
            lng: latLng.lng || latlong.lng, id
          }
          : {
            ...values, phoneNumber, countryCode, lat: latLng.lat || latlong.lat,
            lng: latLng.lng || latlong.lng
          };

        const response = id
          ? await customerManagementApi.updateUser(payload)
          : await customerManagementApi.add(payload);

        toast.success(response?.data?.message, { toastId: "success" });
        navigate(-1);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    },
  });


  return (
    <Box>
      <Helmet>
        <title>{id ? "Edit" : "Add"} Customer</title>
      </Helmet>
      <CardLayout>
        <CardHeader title={`${id ? "Edit" : "Add"} Customer`} />
        <CardContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>
                <Grid item mt={6} xs={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    required
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    name="name"
                    error={Boolean(
                      formik.touched.name && formik.errors.name
                    )}
                    helperText={
                      formik.touched.name && formik.errors.name
                    }
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
                <Grid item mt={6} xs={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    name="email"
                    error={Boolean(formik.touched.email && formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                  />
                </Grid>
                <Grid item xs={6}>
                  <PhoneInput
                    country={"kh"}
                    value={phoneUpdate || formik.values.phoneNumber}
                    onChange={(phone, country) => {
                      setPhoneUpdate(phone)
                      formik.setFieldValue("phoneNumber", phone);
                      formik.setFieldValue("countryCode", `+${country.dialCode}`);
                    }}
                    inputProps={{
                      name: "phoneNumber",
                      required: true,
                    }}
                    inputStyle={{
                      width: "100%",
                      height: "56px",
                    }}
                  />
                  {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                    <div style={{ color: "red", fontSize: "12px", marginLeft: "12px", marginTop: "4px" }}>
                      {formik.errors.phoneNumber}
                    </div>
                  )}
                </Grid>
                {/* <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="ID No./Registration No."
                    required
                    value={formik.values.registrationNumber}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase();
                      if (/^[A-Z0-9]*$/.test(value) && value.length <= 20) {
                        formik.setFieldValue("registrationNumber", value);
                      }
                    }}
                    name="registrationNumber"
                    type="text"
                    error={Boolean(
                      formik.touched.registrationNumber && formik.errors.registrationNumber
                    )}
                    helperText={
                      formik.touched.registrationNumber && formik.errors.registrationNumber
                    }
                  />
                </Grid> */}

                {/* <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="ID No./Registration No."
                  required
                  value={formik.values.registrationNumber}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d{0,20}$/.test(value)) { // Replace 20 with your desired max length
                      formik.setFieldValue("registrationNumber", value);
                    }
                  }}
                  name="registrationNumber"
                  type="text" // Use text to enforce max length
                  error={Boolean(
                    formik.touched.registrationNumber && formik.errors.registrationNumber
                  )}
                  helperText={
                    formik.touched.registrationNumber && formik.errors.registrationNumber
                  }
                />

                </Grid> */}
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="House No."
                    required
                    inputRef={addressRefs.houseNumber}
                    value={formik.values.houseNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    name="houseNumber"
                    error={Boolean(formik.touched.houseNumber && formik.errors.houseNumber)}
                    helperText={formik.touched.houseNumber && formik.errors.houseNumber}
                    inputProps={{ maxLength: 10 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Street No."
                    required
                    value={formik.values.streetNumber}
                    onChange={formik.handleChange}
                    name="streetNumber"
                    error={Boolean(formik.touched.streetNumber && formik.errors.streetNumber)}
                    helperText={formik.touched.streetNumber && formik.errors.streetNumber}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="District"
                    required
                    value={formik.values.district}
                    onChange={formik.handleChange}
                    name="district"
                    error={Boolean(
                      formik.touched.district && formik.errors.district
                    )}
                    helperText={
                      formik.touched.district && formik.errors.district
                    }
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Commune"
                    required
                    value={formik.values.commune}
                    onChange={formik.handleChange}
                    name="commune"
                    error={Boolean(formik.touched.commune && formik.errors.commune)}
                    helperText={formik.touched.commune && formik.errors.commune}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Village"
                    value={formik.values.village}
                    onChange={formik.handleChange}
                    name="village"
                    error={Boolean(
                      formik.touched.village && formik.errors.village
                    )}
                    helperText={formik.touched.village && formik.errors.village}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="City/Province"
                    required
                    value={formik.values.city}
                    onChange={formik.handleChange}
                    name="city"
                    error={Boolean(formik.touched.city && formik.errors.city)}
                    helperText={formik.touched.city && formik.errors.city}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Postal Code"
                    value={formik.values.postalCode}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d{0,6}$/.test(value)) {
                        formik.setFieldValue("postalCode", value);
                      } else {
                        e.preventDefault();
                      }
                    }}
                    name="postalCode"
                    type="text"
                    error={Boolean(
                      formik.touched.postalCode && formik.errors.postalCode
                    )}
                    helperText={formik.touched.postalCode && formik.errors.postalCode}
                  />
                </Grid>

                <Grid item mt={4} xs={12} sx={{ display: "flex", justifyContent: "center" }} >
                  <LoadingButton
                    size="large"
                    type="submit"
                    variant="contained"
                    loading={loading}
                    sx={{ width: "500px" }}
                  >
                    {id ? "Edit" : "Add"} Customer
                  </LoadingButton>
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default AddCustomer;
